import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import type { AlertResumeFilters, AlertType } from '../../types/alertResume';
import { Button, FormField, FormLabel, FormSelect } from '../ui';
import userAccountService from '../../services/user-account.service';

interface AlertResumeFiltersProps {
  filters: AlertResumeFilters;
  onFilterChange: (filters: AlertResumeFilters) => void;
  onResetFilters: () => void;
}

const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  downdetector: 'Downdetector',
  manual: 'Manual',
  erp: 'ERP',
};

function AlertResumeFilters({
  filters,
  onFilterChange,
  onResetFilters,
}: AlertResumeFiltersProps) {
  const { data: userAccounts = [] } = useQuery({
    queryKey: ['user-accounts'],
    queryFn: () => userAccountService.getAccounts(),
  });

  const handleChange = (key: keyof AlertResumeFilters, value: any) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const hasActiveFilters =
    filters.resolved !== undefined ||
    filters.user_account_id !== undefined ||
    filters.alert_type !== undefined;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
      <div className="flex flex-wrap gap-4 items-end">
        <FormField className="flex-1 min-w-[160px]">
          <FormLabel htmlFor="filter-resolved">Status</FormLabel>
          <FormSelect
            id="filter-resolved"
            value={filters.resolved !== undefined ? String(filters.resolved) : ''}
            onChange={e => {
              const v = e.target.value;
              handleChange('resolved', v === '' ? undefined : v === 'true');
            }}
          >
            <option value="">Todos</option>
            <option value="false">Pendentes</option>
            <option value="true">Resolvidos</option>
          </FormSelect>
        </FormField>

        <FormField className="flex-1 min-w-[160px]">
          <FormLabel htmlFor="filter-alert-type">Tipo de Alerta</FormLabel>
          <FormSelect
            id="filter-alert-type"
            value={filters.alert_type ?? ''}
            onChange={e => {
              const v = e.target.value as AlertType | '';
              handleChange('alert_type', v === '' ? undefined : v);
            }}
          >
            <option value="">Todos</option>
            <option value="downdetector">Downdetector</option>
            <option value="manual">Manual</option>
            <option value="erp">ERP</option>
            <option value="resumo">Resumo</option>
          </FormSelect>
        </FormField>

        <FormField className="flex-1 min-w-[160px]">
          <FormLabel htmlFor="filter-user-account">Usuário</FormLabel>
          <FormSelect
            id="filter-user-account"
            value={filters.user_account_id !== undefined ? String(filters.user_account_id) : ''}
            onChange={e => {
              const v = e.target.value;
              handleChange('user_account_id', v === '' ? undefined : parseInt(v));
            }}
          >
            <option value="">Todos</option>
            {userAccounts.map(ua => (
              <option key={ua.id} value={ua.id}>
                {ua.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        {hasActiveFilters && (
          <Button
            type="button"
            variant="secondary"
            onClick={onResetFilters}
            className="whitespace-nowrap self-end"
          >
            <X className="h-4 w-4 mr-2" />
            Limpar Filtros
          </Button>
        )}
      </div>
    </div>
  );
}

export default AlertResumeFilters;
